import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import "./index.css";
import "react-loading-skeleton/dist/skeleton.css";
import { AuthProvider } from "./context/AuthContext";
import { AgencyAuthProvider } from "./context/AgencyAuthContext";
import { ThemeProvider, ForceLightTheme } from "./context/ThemeContext";
import App from "./components/App";
import Login from "./components/Login";
import ResetPassword from "./components/ResetPassword";
import ResetPasswordEmailInput from "./components/ResetPasswordEmailInput";
import Signup from "./components/Signup";
import Dashboard from "./components/Dashboard";
import Settings from "./components/Settings/Settings";
import ProtectedRoute from "./components/ProtectedRoute";
import reportWebVitals from "./reportWebVitals";
import Subscription from "./components/Subscription";
import axios from "axios";
import { isAuthPath } from "./utilities/isAuthPage";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import PurchaseConfirmation from "./components/PurchaseConfirmation";
import Preferences from "./components/Settings/Preferences";
import PostSignup from "./components/PostSignup";
import AgencyLogin from "./components/Agency/Login";
import AgencySignup from "./components/Agency/Signup";
import AgencyDashboard from "./components/Agency/Dashboard";
import AgencyProtectedRoute from "./components/AgencyProtectedRoute";
import AcceptTransfer from "./components/Agency/AcceptTransfer";
import AcceptAdminInvite from "./components/Agency/AcceptAdminInvite";
import AgencyResetPassword from "./components/Agency/ResetPassword";
import AgencyResetPasswordEmailInput from "./components/Agency/ResetPasswordEmailInput";
import SecureTransfer from "./components/pages/SecureTransfer";
import AudioCompress from "./components/pages/AudioCompress";
import ManchesterPDChecklistResults from "./components/pages/ManchesterStudy/ManchesterPDChecklistResults";
import TrialFindingsRoute from "./components/pages/TrialFindings/TrialFindingsRoute";
import SDPDBriefing from "./components/pages/SDPD/SDPDBriefing";
import TrialRequirements from "./components/pages/TrialRequirements/TrialRequirements";
import NotFound from "./components/NotFound";
import AgencyNotFound from "./components/Agency/NotFound";
import AgencyManagement from "./components/AgencyManagement/AgencyManagement";
import UsageDashboard from "./components/AgencyManagement/Usage/UsageDashboard";
import SalesMetrics from "./components/Sales/SalesMetrics";
import FormsRouter from "./components/Forms/FormsRouter";
import FilesRouter from "./components/Files/FilesRouter";
import RedactionsRouter from "./components/Redactions/RedactionsRouter";
import InvestigationsRouter from "./components/Investigations/InvestigationsRouter";
import PoliciesRouter from "./components/Policies/PoliciesRouter";
import FeatureRequestsRouter from "./components/FeatureRequests/FeatureRequestsRouter";
import MFASetup from "./components/MFA/MFASetup";
import MFAChallenge from "./components/MFA/MFAChallenge";
import AccountTransfer from "./components/AccountTransfer";
import PixelRouteTracker from "./components/PixelRouteTracker";
import AcademyRouter from "./academy/components/AcademyRouter";
import { isAcademyStage } from "./academy/stage/isStage";

axios.defaults.withCredentials = true;

// Circuit breaker: Auto-refresh on repeated rate limit errors
let consecutive429Count = 0;
let lastResetTime = Date.now();

axios.interceptors.response.use(
  (response) => {
    consecutive429Count = 0; // Reset on success
    return response;
  },
  (error) => {
    if (error.response?.status === 429) {
      consecutive429Count++;

      // 3 consecutive 429s = trigger refresh
      if (consecutive429Count >= 3) {
        const now = Date.now();

        // Prevent refresh loops (max once per 10 seconds)
        if (now - lastResetTime > 10000) {
          lastResetTime = now;
          console.warn('Rate limit detected - reloading page');
          window.location.reload(); // Hard refresh
        }
      }
    } else {
      consecutive429Count = 0; // Reset on other errors
    }

    // Handle session expiry (CJIS timeout)
    if (error.response?.status === 401 && error.response?.data?.error === "session_expired") {
      const reason = error.response.data.reason;
      const isAgency = window.location.pathname.startsWith("/agency");
      const loginPath = isAgency ? "/agency/login" : "/login";
      window.location.href = `${loginPath}?reason=${reason}`;
    }

    // Server-side paywall: the API refuses officers with no active
    // subscription (subscriptionGuard.ts). Whatever route got them into the
    // app, the first gated request sends them to the paywall. Pages a
    // locked-out officer legitimately sits on are left alone.
    if (error.response?.status === 402 && error.response?.data?.error === "subscription_required") {
      const path = window.location.pathname;
      const staysPut =
        isAuthPath(path) ||
        path.startsWith("/agency") ||
        path.includes("/subscription") ||
        path.includes("/confirmation") ||
        path.includes("/post-signup") ||
        path.includes("/mfa-");
      if (!staysPut) {
        window.location.href = "/subscription";
      }
    }

    // Handle MFA required — skip redirect if already on an MFA page
    if (error.response?.status === 403 && error.response?.data?.error === "mfa_required") {
      const path = window.location.pathname;
      const alreadyOnMfa = path.includes("/mfa-setup") || path.includes("/mfa-challenge");
      if (!alreadyOnMfa) {
        const isAgency = path.startsWith("/agency");
        const mfaConfigured = error.response.data.mfaConfigured;
        if (isAgency) {
          window.location.href = mfaConfigured ? "/agency/mfa-challenge" : "/agency/mfa-setup";
        } else {
          window.location.href = mfaConfigured ? "/mfa-challenge" : "/mfa-setup";
        }
      }
    }

    return Promise.reject(error);
  }
);

// Initialize Sentry. Not in Academy stage mode: the app is then a scripted
// demo inside a lesson's player, running against a fixture API, and nothing
// it does should reach Sentry or the ad pixels.
if (!isAcademyStage) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const renderApp = () => root.render(
  <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
    <ThemeProvider>
      <Router>
      {!isAcademyStage && <PixelRouteTracker />}
      <Routes>
        {/* Agency Management portal route */}
        <Route path="/agency-management" element={<AgencyManagement />} />
        <Route path="/agency-management/usage" element={<UsageDashboard />} />
        <Route path="/sales-metrics" element={<SalesMetrics />} />

        {/* Agency routes with AgencyAuthProvider */}
        <Route
          path="/agency/*"
          element={
            <AgencyAuthProvider>
              <Routes>
                <Route path="login" element={<AgencyLogin />} />
                <Route path="signup" element={<AgencySignup />} />
                <Route path="accept-transfer" element={<AcceptTransfer />} />
                <Route path="accept-admin-invite" element={<AcceptAdminInvite />} />
                <Route path="reset-password" element={<AgencyResetPassword />} />
                <Route path="reset-password/new" element={<AgencyResetPasswordEmailInput />} />
                <Route path="mfa-setup" element={<MFASetup isAgency />} />
                <Route path="mfa-challenge" element={<MFAChallenge isAgency />} />
                <Route element={<AgencyProtectedRoute />}>
                  <Route path="dashboard/*" element={<AgencyDashboard />} />
                </Route>
                {/* Catch-all for invalid agency routes */}
                <Route path="*" element={<AgencyNotFound />} />
              </Routes>
            </AgencyAuthProvider>
          }
        />

        {/* Regular user routes with AuthProvider */}
        <Route
          path="/*"
          element={
            <AuthProvider>
              <Routes>
                <Route path="/" element={<App />} />
                <Route path="/health" element={<h3>The App is Healthy</h3>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/reset-password/new"
                  element={<ResetPasswordEmailInput />}
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/post-signup" element={<PostSignup />} />
                <Route path="/subscription" element={<Subscription />} />
                <Route
                  path="/confirmation"
                  element={<PurchaseConfirmation />}
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/secure-transfer" element={<SecureTransfer />} />
                <Route path="/audio-compress" element={<AudioCompress />} />
                <Route
                  path="/manchester-pd-checklists-results"
                  element={
                    <ForceLightTheme>
                      <ManchesterPDChecklistResults />
                    </ForceLightTheme>
                  }
                />
                {/* Trial findings live at /trial-findings; annual
                    department reviews use /department-review. Both render
                    the same component — the bundle JSON carries
                    report_type which drives the page framing. The URL
                    just makes it obvious which kind of report this is
                    when an agency shares the link. */}
                <Route
                  path="/trial-findings/:slug"
                  element={
                    <ForceLightTheme>
                      <TrialFindingsRoute />
                    </ForceLightTheme>
                  }
                />
                <Route
                  path="/department-review/:slug"
                  element={
                    <ForceLightTheme>
                      <TrialFindingsRoute />
                    </ForceLightTheme>
                  }
                />
                {/* Emailed trial invitation landing page: public, light theme. */}
                <Route
                  path="/trial-requirements"
                  element={
                    <ForceLightTheme>
                      <TrialRequirements />
                    </ForceLightTheme>
                  }
                />
                {/* Interactive briefing accompanying our response to San Diego
                    PD's RFI 30000072-27-E. Shareable external document — light
                    theme only, like the other outward-facing report pages. */}
                <Route
                  path="/sdpd"
                  element={
                    <ForceLightTheme>
                      <SDPDBriefing />
                    </ForceLightTheme>
                  }
                />

                <Route path="/mfa-setup" element={<MFASetup />} />
                <Route path="/mfa-challenge" element={<MFAChallenge />} />
                <Route path="/account-transfer" element={<AccountTransfer />} />

                {/* User Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  {/* The Academy is its own page (own top nav, no sidebar),
                      like a docs site beside the app, so it sits outside the
                      Dashboard shell. Its walkthroughs open the app in a new
                      tab with `?tour=<id>` (see academy/walkthrough.ts). */}
                  <Route path="academy/*" element={<AcademyRouter />} />
                  <Route element={<Dashboard />}>
                    <Route path="cases" element={null} />
                    <Route path="cases/:id" element={null} />
                    <Route path="trash" element={null} />
                    <Route path="trash/:id" element={null} />
                    <Route path="settings" element={<Settings />} />
                    <Route path="preferences" element={<Preferences />} />
                    <Route path="files/*" element={<FilesRouter />} />
                    <Route path="forms/*" element={<FormsRouter />} />
                    <Route path="redactions/*" element={<RedactionsRouter />} />
                    <Route path="feature-requests/*" element={<FeatureRequestsRouter />} />
                    <Route path="investigations/*" element={<InvestigationsRouter />} />
                    <Route path="policies/*" element={<PoliciesRouter />} />
                  </Route>
                </Route>

                {/* Catch-all for invalid regular routes */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          }
        />
      </Routes>
      </Router>
    </ThemeProvider>
  </Sentry.ErrorBoundary>
);

// Academy stage mode installs the fixture API (axios mock adapter, storage
// isolation, fetch guard) before the first render so AuthProvider's boot
// requests already hit it. The module is loaded on demand so none of it
// ships in the normal bundle path.
if (isAcademyStage) {
  import("./academy/stage/installAcademyStage")
    .then((m) => m.default())
    .catch((err) => console.error("[academy] stage install failed", err))
    .then(renderApp);
} else {
  renderApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
